import { MONEY_PRIVACY_CLASS } from "../../../utils/moneyPrivacy";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useTheme } from "../../../context/ThemeContext";
import {
  fetchClients,
  fetchEntries,
  fetchMe,
  fetchRepoHours,
  fetchSummary,
  type HourEntry,
  type SummaryResponse,
  type WorkHoursClientRow,
  type WorkHoursMe,
} from "../services/workHoursService";
import { dateTimeLabel, localeOf, payPeriodPreset, presetRange, rangeDays, today, type RangePreset } from "../lib/heatmap";
import {
  COMPARE_WINDOWS,
  comparisonRange,
  repoDelta,
  repoShares,
  topWithOthers,
  type CompareWindow,
  type DateRange,
} from "../lib/repoComparisons";
import { repoUrl } from "../lib/github";
import { readFailureKey } from "../lib/requestFailure";
import SummaryNotices from "../components/SummaryNotices";
import { moneyAmounts } from "../components/ExpectedKpis";
import { buildThemeClasses, inputClass } from "../theme";

// ---------------------------------------------------------------------------
// Repositorios vinculados: de qué repos han salido las horas del rango, con
// enlace a GitHub, gráficas de horas y % (#1062), comparativos contra el mismo
// tamaño de rango en el pasado (#1062) y, sólo para los dueños, el costo de
// las horas de cada repo (#1063).
//
// De dónde sale cada dato (todo con lo que YA devuelve la API):
//   • horas         — `summary.byRepo` de `workHoursSummary` (una franja que tocó
//                     varios repos se reparte entre ellos; es la cifra buena).
//   • %             — horas del repo / suma de `byRepo` del rango.
//   • comparativos  — el mismo resumen para la ventana movida hacia atrás
//                     (`lib/repoComparisons`), con el mismo alcance.
//   • costo         — `repoCost` del resumen: lo calcula el servidor y SÓLO lo
//                     manda a los dueños. La pantalla nunca lo deriva.
//   • commits       — SHAs distintos de `workHoursEntries` cuyo commit toca el repo.
//   • personas      — usuarios distintos de las franjas que tocan el repo.
//   • último commit — mayor `authoredAt` de esos commits, en hora de CDMX.
//   • clientes      — `workHoursClientList`: el cliente declara sus `repos`.
//
// Alcance por rol (#1063): el servidor decide. Un dev recibe solo lo suyo
// (`resolveUserFilter` le fuerza su `userId`); los dueños eligen equipo o
// persona. El selector solo se pinta si `workHoursMe` dice `isAdmin`, pero
// quitarlo no abre nada: la API ignora el `userId` de quien no administra.
//
// `workHoursEntries` devuelve como máximo las 200 franjas más recientes: cuando
// llega tope, la sección lo dice en vez de presentar cifras incompletas como
// totales. Las horas nunca se ven afectadas (salen del resumen).
// ---------------------------------------------------------------------------

/** Tope de franjas que devuelve `workHoursEntries` (`.slice(0, 200)` en el backend). */
export const ENTRIES_LIMIT = 200;

/** Repos que se grafican por nombre; el resto va en «Otros». */
export const CHART_TOP = 8;

/** Paleta de las gráficas (violeta = marca de la app); «Otros» siempre en gris. */
const PALETTE = ["#8b5cf6", "#0d9488", "#d97706", "#db2777", "#2563eb", "#65a30d", "#dc2626", "#0891b2"];
const OTHERS_COLOR = "#9ca3af";
const PREVIOUS_COLOR = "#94a3b8";

interface RepoRow {
  repo: string;
  hours: number;
  /**
   * #1344: horas registradas A MANO en el repo (entradas que no citan commits).
   * `hours` es lo atribuido por commits. `null` si el servidor aún no manda el desglose.
   */
  manualHours: number | null;
  percent: number;
  /** `null` cuando el detalle no llegó: no se puede contar, y eso NO es cero. */
  commits: number | null;
  people: number | null;
  clients: string[];
  lastCommitAt: string | null;
}

type ComparisonState =
  | { status: "loading"; range: DateRange }
  | { status: "ok"; range: DateRange; byRepo: Map<string, number>; total: number; rangeClamped: boolean }
  | { status: "error"; range: DateRange };

const at = (iso: string | null | undefined): number => {
  const ms = new Date(String(iso)).getTime();
  return Number.isNaN(ms) ? -1 : ms;
};

const push = <T,>(map: Map<string, Set<T>>, key: string, value: T) => {
  const set = map.get(key);
  if (set) set.add(value);
  else map.set(key, new Set([value]));
};

const hoursLabel = (h: number) => `${Math.round(h * 100) / 100} h`;

export default function RepositoriesSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const locale = localeOf(i18n?.language);
  const card = `rounded-2xl border p-4 ${tc.cardBg} ${tc.border}`;
  const axisColor = isDarkMode ? "#9ca3af" : "#6b7280";

  const [me, setMe] = useState<WorkHoursMe | null>(null);
  const [preset, setPreset] = useState<RangePreset>("30d");
  const [range, setRange] = useState(presetRange("30d"));
  // Sólo los dueños eligen persona; vacío = todo el equipo (el servidor manda).
  const [userId, setUserId] = useState("");
  const [data, setData] = useState<SummaryResponse | null>(null);
  const [entries, setEntries] = useState<HourEntry[] | null>(null);
  const [clients, setClients] = useState<WorkHoursClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  // Se guarda la CLAVE i18n, no el texto: así `load` no depende de `t` (que
  // react-i18next reemplaza al cargar el namespace o al cambiar de idioma).
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [entriesErrorKey, setEntriesErrorKey] = useState<string | null>(null);
  const [comparisons, setComparisons] = useState<Partial<Record<CompareWindow, ComparisonState>>>({});
  const [chartWindow, setChartWindow] = useState<CompareWindow>("week");

  useEffect(() => {
    fetchMe()
      .then(setMe)
      .catch(() => setMe(null));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorKey(null);
    const params = { from: range.from, to: range.to, userId: userId || undefined };
    try {
      setData(await fetchSummary(params));
    } catch (e) {
      // Un corte por tiempo se decía igual que un fallo definitivo. No lo es:
      // el dato no llegó, y eso se puede volver a pedir.
      setData(null);
      setErrorKey(readFailureKey(e, { failed: "workHours.loadError", timeout: "workHours.timeoutError" }));
      setLoading(false);
      return;
    }
    // El detalle es complementario: si falla, las horas por repo se muestran
    // igual. Pero no se hace pasar por vacío: sin detalle no hay commits ni
    // personas que contar, y la tabla lo dice con «—», no con un 0 inventado.
    try {
      setEntries(await fetchEntries(params));
      setEntriesErrorKey(null);
    } catch (e) {
      setEntries(null);
      setEntriesErrorKey(
        readFailureKey(e, { failed: "workHours.repositories.detailError", timeout: "workHours.repositories.detailTimeout" }),
      );
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Comparativos (#1062): una consulta por ventana, en paralelo e independientes
  // (si una falla, las demás se muestran). Una respuesta de un rango viejo se
  // descarta: el usuario pudo cambiar de rango mientras llegaba.
  useEffect(() => {
    let cancelled = false;
    const initial: Partial<Record<CompareWindow, ComparisonState>> = {};
    for (const w of COMPARE_WINDOWS) initial[w] = { status: "loading", range: comparisonRange(range, w) };
    setComparisons(initial);
    for (const w of COMPARE_WINDOWS) {
      const r = comparisonRange(range, w);
      fetchRepoHours({ from: r.from, to: r.to, userId: userId || undefined })
        .then((res) => {
          if (cancelled) return;
          const byRepo = new Map(res.byRepo.map((x) => [x.repo, x.hours]));
          const total = res.byRepo.reduce((n, x) => n + x.hours, 0);
          setComparisons((prev) => ({ ...prev, [w]: { status: "ok", range: r, byRepo, total, rangeClamped: res.rangeClamped } }));
        })
        .catch(() => {
          if (cancelled) return;
          setComparisons((prev) => ({ ...prev, [w]: { status: "error", range: r } }));
        });
    }
    return () => {
      cancelled = true;
    };
    // `range` como objeto cambia en cada render del preset: se depende de sus fechas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, userId]);

  // Los clientes no dependen del rango y son de admin: a un dev la API responde
  // 403 y la columna queda vacía (el resto de la sección sí es suya).
  useEffect(() => {
    fetchClients()
      .then(setClients)
      .catch(() => setClients([]));
  }, []);

  const rows = useMemo<RepoRow[]>(() => {
    const shas = new Map<string, Set<string>>();
    const people = new Map<string, Set<string>>();
    const last = new Map<string, string>();
    for (const entry of entries ?? []) {
      for (const commit of entry.commits ?? []) {
        for (const repo of commit.repos ?? []) {
          push(shas, repo, commit.sha);
          push(people, repo, entry.userName ?? entry.userId);
          const previous = last.get(repo);
          if (commit.authoredAt && (!previous || at(commit.authoredAt) > at(previous))) last.set(repo, commit.authoredAt);
        }
      }
    }
    const byRepoClients = new Map<string, string[]>();
    for (const client of clients) {
      for (const repo of client.repos ?? []) byRepoClients.set(repo, [...(byRepoClients.get(repo) ?? []), client.name]);
    }
    const known = entries !== null;
    // #1344: lo registrado a mano por repo sale del desglose del resumen. Un repo
    // con horas a mano y sin commits en el rango también merece su renglón.
    const breakdown = data?.summary.repoBreakdown ?? null;
    const manual = new Map((breakdown?.byRepo ?? []).filter((x) => x.repo).map((x) => [x.repo, Math.round((x.withoutCommitsMinutes / 60) * 100) / 100]));
    const byCommits = data?.summary.byRepo ?? [];
    const seen = new Set(byCommits.map((r) => r.repo));
    const onlyManual = [...manual.entries()].filter(([repo, h]) => h > 0 && !seen.has(repo)).map(([repo]) => ({ repo, hours: 0 }));
    return repoShares([...byCommits, ...onlyManual])
      .map((r) => ({
        repo: r.repo,
        hours: r.hours,
        manualHours: breakdown ? manual.get(r.repo) ?? 0 : null,
        percent: r.percent,
        commits: known ? shas.get(r.repo)?.size ?? 0 : null,
        people: known ? people.get(r.repo)?.size ?? 0 : null,
        clients: byRepoClients.get(r.repo) ?? [],
        lastCommitAt: last.get(r.repo) ?? null,
      }))
      .sort((a, b) => b.hours - a.hours || a.repo.localeCompare(b.repo));
  }, [data, entries, clients]);

  const hasManual = rows.some((r) => r.manualHours !== null);
  const othersLabel = t("workHours.repositories.others");
  // Las gráficas miden horas por commits: un repo con solo horas a mano no entra.
  const chartRows = useMemo(
    () =>
      topWithOthers(
        rows.filter((r) => r.hours > 0),
        CHART_TOP,
        othersLabel,
      ),
    [rows, othersLabel],
  );
  const colorOf = (repo: string, i: number) => (repo === othersLabel && i >= CHART_TOP ? OTHERS_COLOR : PALETTE[i % PALETTE.length]);

  // Costo por repo (#1063): sólo existe si el servidor lo mandó (dueños).
  const repoCost = data?.repoCost ?? null;
  const costByRepo = useMemo(() => new Map((repoCost?.byRepo ?? []).map((c) => [c.repo, c])), [repoCost]);

  // Comparativo: repos del rango actual ∪ los de las ventanas ya cargadas (un
  // repo que dejó de tener horas también es una variación).
  const compareRepos = useMemo(() => {
    const current = new Map(rows.map((r) => [r.repo, r.hours]));
    const all = new Set(current.keys());
    for (const w of COMPARE_WINDOWS) {
      const c = comparisons[w];
      if (c && c.status === "ok") for (const repo of c.byRepo.keys()) all.add(repo);
    }
    return [...all]
      .map((repo) => ({ repo, hours: current.get(repo) ?? 0 }))
      .sort((a, b) => b.hours - a.hours || a.repo.localeCompare(b.repo));
  }, [rows, comparisons]);

  const selected = comparisons[chartWindow];
  const compareChart = useMemo(() => {
    if (!selected || selected.status !== "ok") return [];
    return compareRepos.slice(0, CHART_TOP).map((r) => ({ repo: r.repo, current: r.hours, previous: selected.byRepo.get(r.repo) ?? 0 }));
  }, [compareRepos, selected]);

  const capped = (entries?.length ?? 0) >= ENTRIES_LIMIT;
  const days = rangeDays(range.from, range.to);
  const windowLabel = (w: CompareWindow) => t(`workHours.repositories.compare.${w}`);

  const presetButton = (p: Exclude<RangePreset, "custom">, title?: string) => (
    <button
      key={p}
      type="button"
      data-testid={`range-${p}`}
      title={title}
      onClick={() => {
        setPreset(p);
        setRange(presetRange(p));
      }}
      className={`rounded-lg px-3 py-2 text-xs font-medium ${
        preset === p ? "bg-violet-600 text-white" : `${tc.buttonHover} ${tc.textSecondary} border ${tc.border}`
      }`}
    >
      {t(`workHours.range.${p}`)}
    </button>
  );

  /** Horas de la ventana y variación con signo y tono: sube en verde, baja en rojo. */
  const delta = (current: number, window: CompareWindow, repo: string) => {
    const c = comparisons[window];
    if (!c || c.status === "loading") return <span className={tc.textSecondary}>…</span>;
    if (c.status === "error") return <span className={tc.textSecondary}>—</span>;
    const d = repoDelta(current, c.byRepo, repo);
    const tone = d.deltaHours > 0 ? "text-emerald-500" : d.deltaHours < 0 ? "text-red-500" : tc.textSecondary;
    const pct = d.deltaPercent === null ? (current > 0 ? t("workHours.repositories.compare.new") : "") : `${d.deltaPercent > 0 ? "+" : ""}${d.deltaPercent}%`;
    return (
      <span className="whitespace-nowrap">
        <span className={tc.text}>{hoursLabel(d.previous)}</span>{" "}
        <span className={`text-[11px] ${tone}`} data-delta={d.deltaHours}>
          ({d.deltaHours > 0 ? "+" : ""}
          {hoursLabel(d.deltaHours)}
          {pct ? ` · ${pct}` : ""})
        </span>
      </span>
    );
  };

  return (
    <div className="w-full max-w-6xl overflow-y-auto p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${tc.text}`}>{t("workHours.menu.repositories")}</h1>
          <p className={`text-sm ${tc.textSecondary}`}>
            {data ? `${data.scope === "team" ? t("workHours.scopeTeam") : t("workHours.scopeMine")} · ` : ""}
            {t("workHours.repositories.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(["7d", "30d", "90d"] as const).map((p) => presetButton(p))}
          {/* Quincena actual / anterior: la misma regla que el tablero (#993). */}
          {(["qCurrent", "qPrevious"] as const).map((p) => {
            const period = payPeriodPreset(p);
            return presetButton(p, t("workHours.range.payPeriodHint", { from: period.from, to: period.to, payDate: period.payDate }));
          })}
          <input
            type="date"
            aria-label={t("workHours.filters.from")}
            value={range.from}
            max={range.to}
            onChange={(e) => {
              setPreset("custom");
              setRange((r) => ({ ...r, from: e.target.value }));
            }}
            className={inputClass(isDarkMode)}
          />
          <input
            type="date"
            aria-label={t("workHours.filters.to")}
            value={range.to}
            min={range.from}
            max={today()}
            onChange={(e) => {
              setPreset("custom");
              setRange((r) => ({ ...r, to: e.target.value }));
            }}
            className={inputClass(isDarkMode)}
          />
          {me?.isAdmin && (
            <select
              aria-label={t("workHours.filters.user")}
              data-testid="repositories-user-filter"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className={inputClass(isDarkMode)}
            >
              <option value="">{t("workHours.filters.team")}</option>
              {(data?.users ?? []).map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.userName}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {errorKey && (
        <div
          data-testid="repositories-error"
          className={`rounded-lg p-3 text-sm ${errorKey === "workHours.timeoutError" ? "bg-amber-500/10 text-amber-600" : "bg-red-500/10 text-red-500"}`}
        >
          <p>{t(errorKey)}</p>
          <button type="button" onClick={() => void load()} className="mt-2 rounded-md border border-current px-2 py-1 text-[11px] font-medium opacity-90">
            <i className="ri-refresh-line mr-1" />
            {t("workHours.retry")}
          </button>
        </div>
      )}
      {/* Rango recortado, lectura al tope o títulos de PR sin resolver. */}
      <SummaryNotices data={data} />
      {entriesErrorKey && !errorKey && (
        <div
          className={`rounded-lg p-3 text-xs ${
            entriesErrorKey === "workHours.repositories.detailTimeout" ? "bg-amber-500/10 text-amber-600" : "bg-red-500/10 text-red-500"
          }`}
          data-testid="repositories-detail-error"
        >
          <p>{t(entriesErrorKey)}</p>
          <button type="button" onClick={() => void load()} className="mt-2 rounded-md border border-current px-2 py-1 text-[11px] font-medium opacity-90">
            <i className="ri-refresh-line mr-1" />
            {t("workHours.retry")}
          </button>
        </div>
      )}
      {loading && !data && <p className={`text-sm ${tc.textSecondary}`}>{t("workHours.loading")}</p>}

      {data && chartRows.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className={card} data-testid="repo-chart-hours">
            <p className={`mb-2 text-sm font-semibold ${tc.text}`}>{t("workHours.repositories.chartHours")}</p>
            <div style={{ height: Math.max(160, chartRows.length * 32 + 40) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartRows} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} horizontal={false} />
                  <XAxis type="number" tick={{ fill: axisColor, fontSize: 11 }} />
                  <YAxis type="category" dataKey="repo" width={140} tick={{ fill: axisColor, fontSize: 11 }} />
                  <Tooltip formatter={(v) => [hoursLabel(Number(v)), t("workHours.kpi.hours")]} />
                  <Bar dataKey="hours" radius={[0, 4, 4, 0]}>
                    {chartRows.map((r, i) => (
                      <Cell key={r.repo} fill={colorOf(r.repo, i)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className={card} data-testid="repo-chart-share">
            <p className={`mb-2 text-sm font-semibold ${tc.text}`}>{t("workHours.repositories.chartShare")}</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={chartRows} dataKey="hours" nameKey="repo" innerRadius="50%" outerRadius="80%" paddingAngle={1}>
                    {chartRows.map((r, i) => (
                      <Cell key={r.repo} fill={colorOf(r.repo, i)} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, _n, item) => [`${(item?.payload as { percent?: number } | undefined)?.percent ?? 0}% · ${hoursLabel(Number(v))}`, String(_n)]}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="mt-2 space-y-0.5 text-xs" data-testid="repo-share-list">
              {chartRows.map((r, i) => (
                <li key={r.repo} className={`flex items-center justify-between gap-2 ${tc.text}`}>
                  <span className="flex items-center gap-1.5 truncate">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: colorOf(r.repo, i) }} />
                    {r.repo}
                  </span>
                  <span className="font-semibold">{r.percent}%</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {data && (
        <div className={card}>
          <p className={`mb-3 text-xs ${tc.textSecondary}`}>{t("workHours.repositories.count", { count: rows.length })}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="repositories-table">
              <thead>
                <tr className={`text-left text-xs ${tc.textSecondary}`}>
                  <th className="py-1">{t("workHours.repositories.repo")}</th>
                  {/* #1344: con el desglose, la columna de horas se nombra por lo que es
                      (atribuidas por commits) y a su lado van las registradas a mano.
                      %, costo, gráficas y comparativo siguen midiendo las de commits. */}
                  {hasManual ? (
                    <>
                      <th className="py-1 text-right" title={t("workHours.repositories.byCommitsHint")} data-testid="repositories-by-commits-header">
                        {t("workHours.repositories.byCommits")}
                      </th>
                      <th className="py-1 text-right" title={t("workHours.repositories.manualHint")}>
                        {t("workHours.repositories.manual")}
                      </th>
                    </>
                  ) : (
                    <th className="py-1 text-right">{t("workHours.kpi.hours")}</th>
                  )}
                  <th className="py-1 text-right">{t("workHours.repositories.share")}</th>
                  {repoCost && (
                    <th className="py-1 text-right" title={t("workHours.repositories.costHint")} data-testid="repositories-cost-header">
                      {t("workHours.repositories.cost")}
                    </th>
                  )}
                  <th className="py-1 text-right">{t("workHours.kpi.commits")}</th>
                  <th className="py-1 text-right">{t("workHours.repositories.people")}</th>
                  <th className="py-1">{t("workHours.repositories.clients")}</th>
                  <th className="py-1">{t("workHours.repositories.lastCommit")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const url = repoUrl(r.repo);
                  const cost = costByRepo.get(r.repo);
                  return (
                    <tr key={r.repo} data-testid={`repo-row-${r.repo}`} className={`border-t ${tc.border} ${tc.text}`}>
                      <td className="py-1.5">
                        {url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={t("workHours.repositories.openOnGithub", { repo: r.repo })}
                            className="inline-flex items-center gap-1 font-medium text-violet-500 hover:underline"
                          >
                            <i className="ri-github-line" />
                            {r.repo}
                            <i className="ri-external-link-line text-[11px] opacity-70" />
                          </a>
                        ) : (
                          <span className="font-medium">{r.repo}</span>
                        )}
                      </td>
                      <td className="py-1.5 text-right font-semibold" data-testid={`repo-by-commits-${r.repo}`}>
                        {r.hours} h
                      </td>
                      {hasManual && (
                        <td className="py-1.5 text-right" data-testid={`repo-manual-${r.repo}`}>
                          {r.manualHours ? `${r.manualHours} h` : "—"}
                        </td>
                      )}
                      <td className="py-1.5 text-right" data-testid={`repo-share-${r.repo}`}>
                        {r.percent}%
                      </td>
                      {repoCost && (
                        <td className={`py-1.5 text-right text-xs whitespace-nowrap ${MONEY_PRIVACY_CLASS}`} data-testid={`repo-cost-${r.repo}`}>
                          {cost && Object.keys(cost.byCurrency).length ? moneyAmounts(cost.byCurrency, i18n?.language) : "—"}
                          {cost && cost.unpricedHours > 0 && (
                            <span className={`block text-[11px] ${tc.textSecondary}`}>
                              {t("workHours.repositories.costUnpricedHours", { hours: cost.unpricedHours })}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="py-1.5 text-right">{r.commits ?? "—"}</td>
                      <td className="py-1.5 text-right">{r.people ?? "—"}</td>
                      <td className="py-1.5 text-xs">
                        {r.clients.length ? (
                          r.clients.join(", ")
                        ) : (
                          <span className={tc.textSecondary}>{t("workHours.repositories.noClients")}</span>
                        )}
                      </td>
                      <td className="py-1.5 text-xs whitespace-nowrap">{r.lastCommitAt ? dateTimeLabel(r.lastCommitAt, locale) : "—"}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={repoCost ? 8 : 7} className={`py-3 text-center text-xs ${tc.textSecondary}`}>
                      {t("workHours.repositories.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {repoCost && Object.keys(repoCost.byCurrency).length > 0 && (
            <p className={`mt-3 text-xs font-medium ${MONEY_PRIVACY_CLASS} ${tc.text}`} title={t("workHours.repositories.costHint")} data-testid="repo-cost-total">
              <i className="ri-money-dollar-circle-line mr-1" />
              {t("workHours.repositories.costTotal", { amount: moneyAmounts(repoCost.byCurrency, i18n?.language) })}
            </p>
          )}
          {repoCost && repoCost.unpricedUsers.length > 0 && (
            <p className={`mt-1 text-[11px] ${tc.textSecondary}`} data-testid="repo-cost-unpriced">
              {t("workHours.repositories.costUnpriced", { names: repoCost.unpricedUsers.map((u) => u.name).join(", ") })}
            </p>
          )}
          {rows.length > 0 && <p className={`mt-3 text-[11px] ${tc.textSecondary}`}>{t("workHours.repositories.derived")}</p>}
          {rows.length > 0 && hasManual && <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>{t("workHours.repositories.manualOnlyHint")}</p>}
          {capped && (
            <p className="mt-2 rounded-lg bg-amber-500/10 p-2 text-[11px] text-amber-500" data-testid="repositories-capped">
              {t("workHours.repositories.capped", { limit: ENTRIES_LIMIT })}
            </p>
          )}
        </div>
      )}

      {data && (
        <div className={card} data-testid="repo-compare">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className={`text-sm font-semibold ${tc.text}`}>{t("workHours.repositories.compare.title")}</p>
              <p className={`text-xs ${tc.textSecondary}`}>{t("workHours.repositories.compare.subtitle", { days })}</p>
            </div>
            <div className="flex flex-wrap gap-1" role="group">
              {COMPARE_WINDOWS.map((w) => {
                const r = comparisons[w]?.range ?? comparisonRange(range, w);
                return (
                  <button
                    key={w}
                    type="button"
                    aria-pressed={chartWindow === w}
                    data-testid={`compare-window-${w}`}
                    title={t("workHours.repositories.compare.windowHint", { from: r.from, to: r.to })}
                    onClick={() => setChartWindow(w)}
                    className={`rounded-md px-2 py-1 text-xs ${chartWindow === w ? "bg-violet-600 text-white" : `${tc.textSecondary} ${tc.buttonHover}`}`}
                  >
                    {windowLabel(w)}
                  </button>
                );
              })}
            </div>
          </div>
          {selected?.status === "ok" && compareChart.length > 0 && (
            <div className="h-64" data-testid="repo-compare-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={compareChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} vertical={false} />
                  <XAxis dataKey="repo" tick={{ fill: axisColor, fontSize: 10 }} interval={0} />
                  <YAxis tick={{ fill: axisColor, fontSize: 11 }} />
                  <Tooltip formatter={(v) => hoursLabel(Number(v))} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="current" name={t("workHours.repositories.compare.current")} fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="previous" name={windowLabel(chartWindow)} fill={PREVIOUS_COLOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {selected?.status === "error" && (
            <p className="rounded-lg bg-red-500/10 p-2 text-xs text-red-500" data-testid="repo-compare-error">
              {t("workHours.repositories.compare.error", { window: windowLabel(chartWindow) })}
            </p>
          )}
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="repo-compare-table">
              <thead>
                <tr className={`text-left text-xs ${tc.textSecondary}`}>
                  <th className="py-1">{t("workHours.repositories.repo")}</th>
                  <th className="py-1 text-right" title={t("workHours.repositories.compare.windowHint", { from: range.from, to: range.to })}>
                    {t("workHours.repositories.compare.current")}
                  </th>
                  {COMPARE_WINDOWS.map((w) => {
                    const r = comparisons[w]?.range ?? comparisonRange(range, w);
                    return (
                      <th key={w} className="py-1 text-right" title={t("workHours.repositories.compare.windowHint", { from: r.from, to: r.to })}>
                        {windowLabel(w)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {compareRepos.map((r) => (
                  <tr key={r.repo} data-testid={`compare-row-${r.repo}`} className={`border-t ${tc.border} ${tc.text}`}>
                    <td className="py-1.5 font-medium">{r.repo}</td>
                    <td className="py-1.5 text-right font-semibold">{hoursLabel(r.hours)}</td>
                    {COMPARE_WINDOWS.map((w) => (
                      <td key={w} className="py-1.5 text-right text-xs" data-testid={`compare-${w}-${r.repo}`}>
                        {delta(r.hours, w, r.repo)}
                      </td>
                    ))}
                  </tr>
                ))}
                {compareRepos.length === 0 && (
                  <tr>
                    <td colSpan={2 + COMPARE_WINDOWS.length} className={`py-3 text-center text-xs ${tc.textSecondary}`}>
                      {t("workHours.repositories.compare.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className={`mt-2 text-[11px] ${tc.textSecondary}`}>{t("workHours.repositories.compare.rule")}</p>
        </div>
      )}
    </div>
  );
}
